import store from '~/store'
import { selectDockCollapsed, setDockCollapsed } from '~/store/ui'
import { parentOf } from './columnDrop'
import { MONITOR_IDS } from './ids'

// dockview collapses only edge groups, so constraints pin a grid group to its header; `toJSON` drops them, hence `ui.dock.collapsed`. Live collapsed groups → the height each expands back to.
const collapsed = new Map()

// dockview's own group minimum, for a panel registered without one.
const DEFAULT_MIN_HEIGHT = 100

const headerHeight = group =>
	parseFloat(getComputedStyle(group.element).getPropertyValue('--dv-tabs-and-actions-container-height')) || 0

export const isCollapsed = group => collapsed.has(group)

// ⚠️ Uses dockview internals (`api.component.gridview`), as `columnDrop.js` does. Collapsed groups count as not expanded, so a column never ends up all headers.
function canCollapse(api, group) {
	if (group.api.location.type !== 'grid' || group.panels.some(p => MONITOR_IDS.includes(p.id))) return false
	const parent = parentOf(api.component.gridview.root, group)
	if (parent?.orientation !== 'VERTICAL') return false
	return parent.children.some(
		c => c.view !== group && (c.children || (c.view.api.isVisible && !collapsed.has(c.view)))
	)
}

function pin(group, height) {
	group.api.setConstraints({ minimumHeight: height, maximumHeight: height })
	group.api.setSize({ height })
}

// Panels outside the layout (closed, or disallowed by this mode) keep their stored height.
function save(api) {
	const stored = selectDockCollapsed(store.getState())
	const map = { ...stored }
	for (const p of api.panels) delete map[p.id]
	for (const [group, height] of collapsed) {
		if (!api.groups.includes(group)) collapsed.delete(group)
		else for (const p of group.panels) map[p.id] = height
	}
	if (JSON.stringify(map) !== JSON.stringify(stored)) store.dispatch(setDockCollapsed(map))
}

const ANIM_MS = 150
let animEnd = 0
let animFrame = null

// ⚠️ dockview places `renderer: 'always'` content from a box measured at the transition's start; left there it covers the group below.
function trackOverlays(api, root) {
	if (animFrame) return
	const step = () => {
		api.component.overlayRenderContainer.updateAllPositions()
		if (performance.now() < animEnd) animFrame = requestAnimationFrame(step)
		else {
			animFrame = null
			root.removeAttribute('data-animating')
		}
	}
	animFrame = requestAnimationFrame(step)
}

// The attribute and a forced style flush come before dockview writes the new geometry, so the CSS transition has a start value.
function animated(api, group, resize) {
	const root = group.element.closest('.dockview-theme-oylo')
	if (root) {
		root.style.setProperty('--dock-anim-ms', `${ANIM_MS}ms`)
		root.setAttribute('data-animating', '')
		void root.offsetHeight
		animEnd = performance.now() + ANIM_MS + 50
		trackOverlays(api, root)
	}
	resize()
}

// Explicit constraints cannot be cleared, only overwritten: the group's tabs' tallest minimum stands in for dockview's per-active-tab one.
function expand(api, group, { height = collapsed.get(group), animate = true } = {}) {
	if (!collapsed.has(group)) return false
	collapsed.delete(group)
	const minimumHeight = Math.max(...group.panels.map(p => p.minimumHeight ?? DEFAULT_MIN_HEIGHT))
	const resize = () => {
		group.api.setConstraints({ minimumHeight, maximumHeight: Number.MAX_SAFE_INTEGER })
		group.api.setSize({ height: Math.max(height, minimumHeight) })
	}
	if (animate) animated(api, group, resize)
	else resize()
	return true
}

// Saves even when nothing expanded: a panel added back drops the height it kept while closed.
export function expandGroup(api, group) {
	expand(api, group)
	save(api)
}

function collapse(api, group, height, animate = true) {
	const header = headerHeight(group)
	if (!header || !canCollapse(api, group)) return false
	collapsed.set(group, height)
	if (animate) animated(api, group, () => pin(group, header))
	else pin(group, header)
	return true
}

export function toggleCollapse(api, group) {
	if (!api) return
	if (!expand(api, group)) collapse(api, group, group.height)
	save(api)
}

const findSash = (node, sash) => {
	if (!node?.children) return null
	const index = node.splitview.sashes.findIndex(s => s.container === sash)
	if (index >= 0) return { branch: node, index }
	for (const child of node.children) {
		const hit = findSash(child, sash)
		if (hit) return hit
	}
	return null
}

// ⚠️ dockview internals (`splitview.sashes`); dragging back resumes dockview's drag through a synthetic `pointerdown`, which re-enters here.
export function collapseOnOvershoot(api, event) {
	const sash = event.target.closest?.('.dv-sash')
	if (!api || !sash || event.button !== 0) return
	const hit = findSash(api.component.gridview.root, sash)
	if (hit?.branch.orientation !== 'VERTICAL') return
	const { branch, index } = hit
	const sides = [index, index + 1]
		.map((i, side) => {
			const group = branch.children[i].view
			if (!group || collapsed.has(group) || !canCollapse(api, group)) return null
			const size = branch.splitview.getViewSize(i)
			const { minimumSize } = branch.splitview.viewItems[i]
			return { group, size, room: size - minimumSize, resist: minimumSize / 2, sign: side ? 1 : -1 }
		})
		.filter(Boolean)
	if (!sides.length) return
	const doc = sash.ownerDocument
	const root = sash.closest('.dockview-theme-oylo')
	const start = event.clientY
	const overshoot = (e, s) => (e.clientY - start) * s.sign - s.room
	let active = null
	// The synthetic pointercancel that ends dockview's drag must not end this one.
	const stop = e => {
		if (e && !e.isTrusted) return
		doc.removeEventListener('pointermove', move)
		doc.removeEventListener('pointerup', stop)
		doc.removeEventListener('pointercancel', stop)
		root?.removeAttribute('data-sash-collapsed')
	}
	const setCollapsed = on => {
		if (on) collapse(api, active.group, active.size, false)
		else expand(api, active.group, { height: 0, animate: false })
		root?.toggleAttribute('data-sash-collapsed', collapsed.has(active.group))
		save(api)
	}
	// Expanded back at the collapse threshold, the group holds its minimum until the pointer reaches the sash, then dockview's drag resumes.
	const move = e => {
		if (!active) {
			const side = sides.find(s => overshoot(e, s) > s.resist)
			if (!side) return
			doc.dispatchEvent(new PointerEvent('pointercancel'))
			if (!collapse(api, side.group, side.size, false)) return stop()
			active = side
			root?.setAttribute('data-sash-collapsed', '')
			save(api)
			return
		}
		const over = overshoot(e, active)
		const isCollapsed = collapsed.has(active.group)
		if (over > active.resist) {
			if (!isCollapsed) setCollapsed(true)
			return
		}
		if (isCollapsed) setCollapsed(false)
		if (over > 0) return
		stop()
		sash.dispatchEvent(
			new PointerEvent('pointerdown', {
				bubbles: true,
				button: 0,
				clientX: e.clientX,
				clientY: e.clientY,
				pointerId: e.pointerId,
			})
		)
	}
	doc.addEventListener('pointermove', move)
	doc.addEventListener('pointerup', stop)
	doc.addEventListener('pointercancel', stop)
}

/* After a layout change: a collapsed group that lost its last expanded column-mate expands. A maximize hides every column-mate, so it changes nothing. */
export function reconcileCollapsed(api) {
	if (!collapsed.size || api.hasMaximizedGroup()) return
	for (const group of [...collapsed.keys()]) {
		if (api.groups.includes(group) && !canCollapse(api, group)) expand(api, group)
	}
	save(api)
}

/* Hydration: a group holding a tab stored as collapsed collapses again. */
export function restoreCollapsed(api) {
	collapsed.clear()
	const stored = selectDockCollapsed(store.getState())
	for (const group of api.groups) {
		const height = group.panels.map(p => stored[p.id]).find(h => h !== undefined)
		const header = headerHeight(group)
		if (height === undefined || !header || !canCollapse(api, group)) continue
		collapsed.set(group, height)
		pin(group, header)
	}
	save(api)
}
